/**
 * チャットメッセージリストコンポーネント（受講生画面）
 *
 * LecturePage の renderChatMessages() からの切り出し。
 * SP用・PC用の2箇所から同一コンポーネントを使用する。
 *
 * - React.memo 化により、親（LecturePage）の再レンダーでは
 *   メッセージが変わらない限りリスト・行とも再レンダーされない
 * - 表示上限（直近 CHAT_DISPLAY_LIMIT 件のみ DOM 化）
 * - 最上部付近へのスクロールで過去分を自動展開（表示位置は維持）
 * - 新着メッセージへの自動追従（最下部付近にいる場合のみ）
 */

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ChatMessage } from '../../types/chat';

/** 初期状態で DOM 化するメッセージ件数（スクロールで拡張） */
const CHAT_DISPLAY_LIMIT = 300;
/** この距離まで最上部に近づいたら過去分を展開する */
const TOP_THRESHOLD_PX = 50;
/** 最下部からこの距離以内なら新着に自動追従する */
const BOTTOM_THRESHOLD_PX = 50;

interface ChatMessageRowProps {
  message: ChatMessage;
}

const ChatMessageRow = React.memo(function ChatMessageRow({ message }: ChatMessageRowProps) {
  const timestamp = message.timestamp
    ? new Date(message.timestamp).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })
    : '';
  return (
    <div
      className={`chat-message ${message.userType === 'instructor' || message.userType === 'admin' ? 'instructor' : ''} ${message.userType === 'question' ? 'question' : ''}`}
    >
      <div className="chat-message-row">
        <div className="chat-message-text">{message.message}</div>
        {timestamp && <div className="chat-message-time">{timestamp}</div>}
      </div>
    </div>
  );
});

export interface ChatMessagesProps {
  messages: ChatMessage[];
  isLoading: boolean;
  errorMessage?: string | null;
  /** PC版で従来付与していた id 属性（例: "chat-messages"） */
  containerId?: string;
}

export const ChatMessages = React.memo(function ChatMessages({
  messages,
  isLoading,
  errorMessage,
  containerId,
}: ChatMessagesProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [displayLimit, setDisplayLimit] = useState(CHAT_DISPLAY_LIMIT);

  const visibleMessages = useMemo(
    () => (messages.length > displayLimit ? messages.slice(-displayLimit) : messages),
    [messages, displayLimit]
  );

  // 自動追従の有効/無効（最下部付近にいるかどうか）
  const shouldAutoScrollRef = useRef(true);
  // 履歴読込完了時の初回最下部スクロールを一度だけ行うためのフラグ
  const hasInitialScrolledRef = useRef(false);
  // displayLimit 拡張の多重発火防止
  const expandPendingRef = useRef(false);
  // 先頭への追加（prepend）検出用
  const prevFirstIdRef = useRef<string | null>(null);
  const prevLastIdRef = useRef<string | null>(null);
  const prevScrollHeightRef = useRef(0);

  /**
   * 履歴読み込み完了後、最下部へ1回だけスクロール
   */
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!isLoading && visibleMessages.length > 0 && !hasInitialScrolledRef.current && el) {
      el.scrollTop = el.scrollHeight;
      shouldAutoScrollRef.current = true;
      hasInitialScrolledRef.current = true;
      prevScrollHeightRef.current = el.scrollHeight;
    }
  }, [isLoading, visibleMessages.length]);

  /**
   * メッセージ変化時のスクロール制御
   * - 先頭に過去分が展開された場合: scrollHeight 差分で scrollTop を補正し表示位置を維持
   * - 末尾に新着が追加された場合: 最下部付近にいるときのみ自動追従
   */
  useLayoutEffect(() => {
    expandPendingRef.current = false;

    const el = containerRef.current;
    const first = visibleMessages[0];
    const last = visibleMessages[visibleMessages.length - 1];

    if (el) {
      const prevFirstId = prevFirstIdRef.current;
      const prevLastId = prevLastIdRef.current;

      const isPrepended =
        !!first &&
        prevFirstId !== null &&
        first.id !== prevFirstId &&
        visibleMessages.some((m) => m.id === prevFirstId);
      const isAppended = !!last && prevLastId !== null && last.id !== prevLastId;

      if (isPrepended) {
        el.scrollTop += el.scrollHeight - prevScrollHeightRef.current;
      } else if (isAppended && shouldAutoScrollRef.current) {
        el.scrollTop = el.scrollHeight;
      }
      prevScrollHeightRef.current = el.scrollHeight;
    }

    prevFirstIdRef.current = first ? first.id : null;
    prevLastIdRef.current = last ? last.id : null;
  }, [visibleMessages]);

  /**
   * タブ切り替え等で非表示→表示になった際に最下部へ追従する
   * （非表示中は scrollHeight が 0 になりスクロール位置が失われるため）
   */
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => {
      if (shouldAutoScrollRef.current && el.scrollHeight > 0) {
        el.scrollTop = el.scrollHeight;
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  /**
   * スクロールハンドラ
   * - 最下部付近かどうかで新着への自動追従を切り替え
   * - 最上部付近で過去分を自動展開
   */
  const handleScroll = () => {
    const el = containerRef.current;
    if (!el) return;

    const isAtBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + BOTTOM_THRESHOLD_PX;
    shouldAutoScrollRef.current = isAtBottom;

    const isNearTop = el.scrollTop <= TOP_THRESHOLD_PX;
    if (isNearTop && messages.length > displayLimit && !expandPendingRef.current) {
      expandPendingRef.current = true;
      setDisplayLimit((prev) => prev + CHAT_DISPLAY_LIMIT);
    }
  };

  let content: React.ReactNode;
  if (isLoading) {
    content = (
      <div className="chat-message" style={{ textAlign: 'center', opacity: 0.6 }}>
        <div className="chat-message-row">
          <div className="chat-message-text">読み込み中...</div>
        </div>
      </div>
    );
  } else if (errorMessage) {
    content = (
      <div className="chat-message" style={{ textAlign: 'center', opacity: 0.6 }}>
        <div className="chat-message-row">
          <div className="chat-message-text" style={{ color: '#dc2626' }}>
            チャット接続エラー: {errorMessage}
          </div>
        </div>
      </div>
    );
  } else {
    content = visibleMessages.map((msg) => <ChatMessageRow key={msg.id} message={msg} />);
  }

  return (
    <div
      className="chat-messages"
      id={containerId}
      ref={containerRef}
      aria-label="チャットメッセージ"
      onScroll={handleScroll}
    >
      {content}
    </div>
  );
});
